import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import { nameofFactory, getNotificationObject, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import TextField from "../../components/Editing/TextField";
import NumberField from "../../components/Editing/NumberField";
import ToggleField from "../../components/Editing/ToggleField";
import MenuButton from "../../components/MenuButton/MenuButton";
import { info } from "react-notification-system-redux";

type StateProps = {
  editedIoNodePoint: Api.IoNodePoint;
  storedIoNodePoint: Api.IoNodePoint;
  clipboard: State.ClipBoard;
  pdApi: State.PdApi;
  ioConfig: State.IoConfig;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
  showInfo: typeof info;
};

type OwnProps = {
  toggleModal: () => void;
};

interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const standardBoardFields = nameofFactory<Api.IoNodePoint>();

export type EditIoNodePointProps = StateProps & DispatchProps & OwnProps;

export class EditIoNodePoint extends React.Component<EditIoNodePointProps> {
  state = {
    hasCopied: false
  };
  componentDidUpdate(prevProps: Api.AnyItem): void {
    if (prevProps.editedIoNodePoint.id !== this.props.editedIoNodePoint.id) {
      this.setState({ hasCopied: false });
    }
  }
  getActions = (): Api.StandardAction[] | null | undefined => {
    const actions = this.props.ioConfig.ionodePoints.find(
      point => point.id === this.props.editedIoNodePoint.id
    )?.ionodePointActions;
    return actions;
  };
  ionodePointActions = this.getActions();
  copyActions = (): void => {
    if (this.ionodePointActions) {
      this.props.showInfo(getNotificationObject("info", "Actions copied to clipboard"));
      this.setState({ hasCopied: true });
      this.props.editActions.copyObject({
        option: "ionodePointActions",
        data: this.ionodePointActions
      });
    }
  };
  pasteActions = (): void => {
    if (this.props.clipboard.option === "ionodePointActions") {
      this.props.showInfo(
        getNotificationObject("info", "Actions pasted to current Point, save to confirm")
      );
      this.props.editActions.pasteObject();
    } else {
      this.props.showInfo(
        getNotificationObject("info", "Can't paste - Wrong data type in clipboard")
      );
    }
  };
  render(): JSX.Element {
    const { editedIoNodePoint, storedIoNodePoint, activeUser, toggleModal, clipboard } = this.props;
    const isNew = !storedIoNodePoint;

    const error = this.isFieldValid(standardBoardFields("ioindex"));
    const nameError = this.isFieldValid(standardBoardFields("ioname"));

    return (
      <Editor
        itemName={"Io Node Point"}
        originalObject={storedIoNodePoint}
        editedObject={editedIoNodePoint}
        hasWritePermission={hasWriteAccess(activeUser, "Lift")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("ionodePoints")}
        create={() => this.props.editActions.add("ionodePoints")}
        remove={{ callback: toggleModal, disabled: isNew }}
      >
        <div className="point-editor-row">
          <div>
            <NumberField
              id="index"
              value={editedIoNodePoint.ioindex}
              label="Index"
              isError={error.errorStatus}
              errorMessage={error.errorMsg}
              onChange={(ioindex: number) =>
                this.props.editActions.setEdited({ ...editedIoNodePoint, ioindex })
              }
            />
          </div>
          <div>
            <ToggleField
              labels={{ left: "Input", right: "Output" }}
              id="isInput"
              checked={!editedIoNodePoint.isInput}
              onClick={() =>
                this.props.editActions.setEdited({
                  ...editedIoNodePoint,
                  isInput: !editedIoNodePoint.isInput
                })
              }
            />
          </div>
        </div>
        <TextField
          id="name"
          label="Name"
          value={editedIoNodePoint.ioname}
          isError={nameError.errorStatus}
          errorMessage={nameError.errorMsg}
          onChange={(ioname: string) =>
            this.props.editActions.setEdited({ ...editedIoNodePoint, ioname })
          }
        />
        {hasWriteAccess(activeUser, "Lift") && (
          <div className="editor-action-list">
            <h3>Manage I/O Actions</h3>
            <MenuButton
              type="Add"
              handleOnClick={() =>
                this.props.editActions.addAction(editedIoNodePoint.id || "", "ionodePointActions")
              }
              customText="ADD ACTION"
              disabled={isNew}
            />
            {this.ionodePointActions && (
              <MenuButton
                type="Copy"
                handleOnClick={() => {
                  this.copyActions();
                }}
                customText="COPY ALL ACTION(S)"
                disabled={isNew || this.state.hasCopied}
              />
            )}
            <MenuButton
              type="Paste"
              handleOnClick={() => {
                this.pasteActions();
              }}
              customText="PASTE ACTION(S)"
              disabled={
                editedIoNodePoint?.ionodePointActions?.length !==
                  storedIoNodePoint?.ionodePointActions?.length ||
                clipboard?.option !== "ionodePointActions"
              }
            />
            {clipboard?.option === "ionodePointActions" && (
              <p>There are {clipboard.data.length} action(s) in clipboard</p>
            )}
          </div>
        )}
      </Editor>
    );
  }

  private isFieldValid = (field: string): ErrorStatus => {
    const { editedIoNodePoint, ioConfig } = this.props;
    switch (field) {
      case standardBoardFields("ioindex"):
        if (ioConfig.ionodePoints.length === 0) {
          return { errorStatus: false, errorMsg: "" };
        } else {
          return ioConfig.ionodePoints.findIndex(
            point =>
              point.ioindex === editedIoNodePoint.ioindex &&
              point.id !== editedIoNodePoint.id &&
              editedIoNodePoint.nodeId === point.nodeId &&
              editedIoNodePoint.isInput === point.isInput
          ) === -1
            ? { errorStatus: false, errorMsg: "" }
            : { errorStatus: true, errorMsg: "Index already exists" };
        }
      case standardBoardFields("ioname"):
        return {
          errorStatus: editedIoNodePoint.ioname.trim().length === 0,
          errorMsg: ""
        };
      default:
        return { errorStatus: false, errorMsg: "" };
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedIoNodePoint } = this.props;
    const temp = _.keys(editedIoNodePoint);
    for (const key of temp) {
      if (this.isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedIoNodePoint: state.editView.editedObject,
  storedIoNodePoint: state.editView.storedObject,
  clipboard: state.editView.clipboard,
  pdApi: state.pdApi,
  ioConfig: state.ioConfig,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  showInfo: bindActionCreators(info, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditIoNodePoint);


